import type { CSSProperties } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token, useThemeObserver } from '@atlaskit/tokens';

import Loading from '@atlassian/generative-ai-modal/assets/Loading';

import {
	COVER_IMAGE_FIXED_WIDTH,
	COVER_IMAGE_FIXED_HEIGHT,
} from '@confluence/page-cover-picture/entry-points/pageCoverPictureConstants.ts';
import { getDynamicCoverImageHeight } from '@confluence/page-cover-picture/entry-points/coverPictureHelperFunctions.ts';
import { useEditorWidthObserver } from '@confluence/editor-features/entry-points/useEditorWidthObserver.ts';

const aiPrismColor = {
	'prism.border.step.1': {
		light: '#0065FF',
		dark: '#0065FF80',
	},
	'prism.border.step.2': {
		light: '#0469FF',
		dark: '#0469FF80',
	},
	'prism.border.step.3': {
		light: '#BF63F3',
		dark: '#BF63F380',
	},
	'prism.border.step.4': {
		light: '#FFA900',
		dark: '#FFA90080',
	},
};

const paragraphStyle = xcss({
	// @ts-expect-error
	color: token('color.text.subtlest'),
	// @ts-expect-error
	padding: '0 0 0 4px',
});

const AISuggestedImageHeaderLoadingContainer = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	height: '100%',
	// @ts-expect-error
	backgroundColor: token('elevation.surface'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
});

const AI_SUGGESTED_IMAGE_HEADER = 'ai-suggested-image-header';

// eslint-disable-next-line check-i18n-id-prefix/i18n-id-prefix-with-package-name-defineMessages
const i18n = defineMessages({
	suggestingAHeaderImage: {
		id: 'ai-suggested-image-header.suggestingAHeaderImage',
		description: 'Text to tell user that we are currently loading a suggested image header',
		defaultMessage: 'Looking for a matching header image',
	},
});

type AISuggestedImageHeaderLoadingProps = {
	isUploadingPicture: boolean;
	isAIUnsplashImage: boolean;
	isFixedWidthImageOption?: boolean;
};

export const AISuggestedImageHeaderLoading = ({
	isUploadingPicture,
	isAIUnsplashImage,
	isFixedWidthImageOption,
}: AISuggestedImageHeaderLoadingProps) => {
	const { colorMode } = useThemeObserver();
	const { width } = useEditorWidthObserver({});
	const coverImageWidth = isFixedWidthImageOption ? COVER_IMAGE_FIXED_WIDTH : width;
	const dynamicCoverImageHeight = isFixedWidthImageOption
		? COVER_IMAGE_FIXED_HEIGHT
		: getDynamicCoverImageHeight(coverImageWidth);
	const { formatMessage } = useIntl();

	const aiSuggestedImageLoadingInlineCss: CSSProperties = {
		height: `${dynamicCoverImageHeight - 24}px`,
	};

	/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */
	const aiSuggestedImagePrismBackground = xcss({
		background: `conic-gradient(
			from var(--panel-gradient-angle, 270deg),
				${aiPrismColor['prism.border.step.1'][colorMode ?? 'light']} 0%,
				${aiPrismColor['prism.border.step.2'][colorMode ?? 'light']} 20%,
				${aiPrismColor['prism.border.step.3'][colorMode ?? 'light']} 50%,
				${aiPrismColor['prism.border.step.4'][colorMode ?? 'light']} 56%,
				${aiPrismColor['prism.border.step.1'][colorMode ?? 'light']} 100%
			)`,
		// @ts-expect-error
		borderRadius: `calc(${
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
			token('radius.small', '3px')
		} + ${token('border.width')})`,
		// @ts-expect-error
		padding: token('border.width'),
		position: 'absolute',
		width: 'calc(100% - 48px)',
		left: '24px',
		top: '24px',
	});
	/* eslint-enable @atlaskit/ui-styling-standard/no-unsafe-values */

	return (
		<>
			{isUploadingPicture && isAIUnsplashImage && (
				<>
					<Box
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
						xcss={aiSuggestedImagePrismBackground}
						style={aiSuggestedImageLoadingInlineCss}
					>
						<Box
							xcss={AISuggestedImageHeaderLoadingContainer}
							testId={`${AI_SUGGESTED_IMAGE_HEADER}-modal-container`}
						>
							{/* TODO: Update the attribute below so it is translated. */}
							{/* eslint-disable-next-line no-restricted-syntax */}
							<Loading alt="test" />
							<Box xcss={paragraphStyle}>{formatMessage(i18n.suggestingAHeaderImage)}</Box>
						</Box>
					</Box>
				</>
			)}
		</>
	);
};
